<script>
  // @ts-check
  import { createEventDispatcher } from "svelte";

  // eslint-disable-next-line no-unused-vars
  import * as Types from "./globalNavigationTypes";
  import { isSmallOrMediumBreakpoint } from "../../utilities/breakpoints";

  import GlobalNavigationDesktop from "./GlobalNavigationDesktop.svelte";
  import GlobalNavigationMobile from "./GlobalNavigationMobile.svelte";

  /** @type {string} id - Primarily used for testing*/
  export let id = undefined;
  /** @type {string} mainLogoAccessibilityText - Logo alt text */
  export let mainLogoAccessibilityText = "The Obama Foundation – Home";
  /** @type {string} currentRoute - The active parent route of the navigation */
  export let currentRoute = "";
  /** @type {Types.MainNavigation} mainNavigation - The data used to populate navigation items */
  export let mainNavigation;
  /** @type {boolean} isExpanded - Determines if the navigation is expanded. */
  export let isExpanded = false;
  /** @type {boolean} hasScrolledDown - Determines if the user has scrolled down */
  export let hasScrolledDown = false;
  /** @type {boolean} isSearchPanelVisible - The data used to populate navigation items */
  export let isSearchPanelVisible = false;
  /** @type {boolean} delayThemeFadeOnNavBar - Delay the theme fade transition on the nav bar */
  export let delayThemeFadeOnNavBar = false;
  /** @type {boolean} animateNavBarItems - If the nav bar items should animate or not */
  export let animateNavBarItems = false;
  /** @type {import('../../utilities/themes.js').StandardThemeName} */
  export let theme = "white";

  let screenWidth;
  let wasLastFocusMobile;

  const dispatch = createEventDispatcher();

  const handleWindowClick = () => {
    dispatch("searchClose");
  };

  const handleWindowKeydown = (e) => {
    if (e.key === "Escape") {
      dispatch("searchClose");
    }
  };

  /**
   * Handles the focusout event.
   */
  const handleFocusIn = () => {
    // Focusin bubbles, focus don't
    // We capture all focusin in the navbar and gets the breakpoint it was focused
    wasLastFocusMobile = isSmallOrMediumBreakpoint(screenWidth);
  };

  /**
   * Handles the focusout event.
   * @param {FocusEvent} event - The focusout event object.
   */
  const handleFocusOut = (event) => {
    // Focusout bubbles, blur don't
    // Use custom data attributes to send focus when breakpoint changes
    if (isSmallOrMediumBreakpoint(screenWidth) !== wasLastFocusMobile) {
      const targetElement = /** @type {HTMLElement} */ (event.target);
      const transmitTransitionFocus = targetElement.dataset.transitionFocus;
      if (!transmitTransitionFocus) {
        return;
      }
      const transitionElementsList = document.querySelectorAll(
        `[data-transition-focus="${transmitTransitionFocus}"]`
      );
      transitionElementsList.forEach((/** @type {HTMLElement} */ transitionElement) => {
        if (targetElement !== transitionElement) {
          transitionElement.focus();
        }
      });
    }
  };
</script>

<svelte:window
  bind:innerWidth={screenWidth}
  on:click={handleWindowClick}
  on:keydown={handleWindowKeydown}
/>

<div
  {id}
  role="link"
  tabindex="0"
  class="contents"
  on:focusin={handleFocusIn}
  on:focusout={handleFocusOut}
  on:click|stopPropagation
  on:keypress|stopPropagation
>
  <GlobalNavigationDesktop
    bind:theme
    bind:isExpanded
    on:searchClose
    {currentRoute}
    {mainNavigation}
    {hasScrolledDown}
    {mainLogoAccessibilityText}
    {isSearchPanelVisible}
    {delayThemeFadeOnNavBar}
    {animateNavBarItems}
  >
    <svelte:fragment slot="utilityNavigation">
      <slot name="utilityNavigation" />
    </svelte:fragment>
    <slot name="searchPanel" slot="searchPanel" />
  </GlobalNavigationDesktop>
  <GlobalNavigationMobile
    bind:isExpanded
    bind:theme
    on:searchClose
    {mainNavigation}
    {mainLogoAccessibilityText}
    {isSearchPanelVisible}
    {hasScrolledDown}
    {delayThemeFadeOnNavBar}
    {animateNavBarItems}
  >
    <svelte:fragment slot="utilityNavigation">
      <slot name="utilityNavigationMobile" />
    </svelte:fragment>
    <slot name="searchPanel" slot="searchPanel" />
  </GlobalNavigationMobile>
</div>
